<template>
	<div class="container-fluid p-4">
		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<div v-if="loading" class="text-center py-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading…</span>
			</div>
			<p class="mt-2 text-muted">Loading trap status…</p>
		</div>

		<div v-else class="row g-4">
			<div class="col-12 col-lg-6">
				<div class="card h-100">
					<div class="card-header">Devices</div>
					<div class="card-body">
						<div class="d-flex gap-4 mb-3">
							<div>
								<div class="display-6 mb-0">{{ deployedDevices.length }}</div>
								<div class="text-muted small">Deployed</div>
							</div>
							<div>
								<div class="display-6 mb-0">{{ idleDevices.length }}</div>
								<div class="text-muted small">Idle</div>
							</div>
						</div>

						<p class="text-muted small mb-3">
							A device is deployed while it has an open (still-set) trap
							instance; otherwise it is idle.
						</p>

						<h2 class="h6 mb-2">Deployed</h2>
						<div v-if="deployedDevices.length === 0" class="text-muted small">
							No devices are deployed.
						</div>
						<ul v-else class="list-group list-group-flush mb-3">
							<li
								v-for="device in deployedDevices"
								:key="device.id"
								class="list-group-item d-flex justify-content-between align-items-center"
							>
								<span>{{ device.serial }}</span>
								<span class="text-muted small">
									<RouterLink :to="`/_/traps/${device.open_instance_id}`">
										Instance #{{ device.open_instance_id }}
									</RouterLink>
								</span>
							</li>
						</ul>

						<h2 class="h6 mb-2">Idle</h2>
						<div v-if="idleDevices.length === 0" class="text-muted small">
							No devices are idle.
						</div>
						<ul v-else class="list-group list-group-flush">
							<li
								v-for="device in idleDevices"
								:key="device.id"
								class="list-group-item d-flex justify-content-between align-items-center"
							>
								<span>{{ device.serial }}</span>
								<span class="badge text-bg-light">{{ device.state }}</span>
							</li>
						</ul>
					</div>
				</div>
			</div>

			<div class="col-12 col-lg-6">
				<div class="card h-100">
					<div class="card-header">Sample labels</div>
					<div class="card-body">
						<div class="d-flex gap-4 mb-3">
							<div>
								<div class="display-6 mb-0">{{ fieldLabels.length }}</div>
								<div class="text-muted small">In the field</div>
							</div>
							<div>
								<div class="display-6 mb-0">{{ labLabels.length }}</div>
								<div class="text-muted small">At the lab</div>
							</div>
							<div>
								<div class="display-6 mb-0">{{ availableLabels.length }}</div>
								<div class="text-muted small">Available</div>
							</div>
						</div>

						<!-- Assumption (refine with the operator): "available" = no
						     open binding; "in the field" = open binding whose trap
						     instance is still set; "at the lab" = open binding whose
						     instance has been collected and is awaiting the lab's scan. -->
						<p class="text-muted small mb-3">
							A label is available while it has no open binding; otherwise it is
							in the field (its instance is still set) or at the lab (its
							instance has been collected, awaiting the scan).
						</p>

						<h2 class="h6 mb-2">In the field</h2>
						<div v-if="fieldLabels.length === 0" class="text-muted small">
							No labels are in the field.
						</div>
						<ul v-else class="list-group list-group-flush mb-3">
							<li
								v-for="label in fieldLabels"
								:key="label.id"
								class="list-group-item d-flex justify-content-between align-items-center"
							>
								<code>{{ label.label }}</code>
								<span class="text-muted small">
									Instance #{{ label.open_binding?.instance_id }}
								</span>
							</li>
						</ul>

						<h2 class="h6 mb-2">At the lab</h2>
						<div v-if="labLabels.length === 0" class="text-muted small">
							No labels are at the lab.
						</div>
						<ul v-else class="list-group list-group-flush mb-3">
							<li
								v-for="label in labLabels"
								:key="label.id"
								class="list-group-item d-flex justify-content-between align-items-center"
							>
								<code>{{ label.label }}</code>
								<span class="text-muted small">
									Instance #{{ label.open_binding?.instance_id }}
								</span>
							</li>
						</ul>

						<h2 class="h6 mb-2">Available</h2>
						<div v-if="availableLabels.length === 0" class="text-muted small">
							No labels are available.
						</div>
						<ul v-else class="list-group list-group-flush">
							<li
								v-for="label in availableLabels"
								:key="label.id"
								class="list-group-item d-flex justify-content-between align-items-center"
							>
								<code>{{ label.label }}</code>
								<span class="badge text-bg-light">{{ label.state }}</span>
							</li>
						</ul>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { RouterLink } from "vue-router";

import { apiClient } from "@/client";
import type { TrapDeviceView, TrapLabelView } from "@/type/api";

const devices = ref<TrapDeviceView[]>([]);
const labels = ref<TrapLabelView[]>([]);
const loading = ref(false);
const error = ref("");

const deployedDevices = computed(() =>
	devices.value.filter(
		(d) => d.open_instance_id !== null && d.open_instance_id !== undefined,
	),
);
const idleDevices = computed(() =>
	devices.value.filter(
		(d) => d.open_instance_id === null || d.open_instance_id === undefined,
	),
);

const availableLabels = computed(() =>
	labels.value.filter((l) => !l.open_binding),
);
const fieldLabels = computed(() =>
	labels.value.filter(
		(l) => l.open_binding && !l.open_binding.instance_collected,
	),
);
const labLabels = computed(() =>
	labels.value.filter(
		(l) => l.open_binding && l.open_binding.instance_collected,
	),
);

async function load() {
	loading.value = true;
	error.value = "";
	try {
		const [deviceRows, labelRows] = await Promise.all([
			apiClient.JSONGet<TrapDeviceView[]>("/api/trap-device"),
			apiClient.JSONGet<TrapLabelView[]>("/api/trap-labels"),
		]);
		devices.value = deviceRows;
		labels.value = labelRows;
	} catch (e: unknown) {
		error.value = e instanceof Error ? e.message : "Failed to load status.";
	} finally {
		loading.value = false;
	}
}

onMounted(load);
</script>
